import React, { useMemo } from 'react';
import { StanDataRow } from '@/components';
import type { DataRowActionButton } from '@/components/core-components/StanDataRow/StanDataRow.types';
import IcAnalytics from '@/assets/icons/ic-analytics.svg';
import { buildFlowColumns, getFlowAnalyticsData, getFlowIcon, getFlowStatus } from '../../logic';
import { getGridTemplate } from '../../constants/gridColumns';
import { useUser } from '@/queries/useUser';
import { useFlowActions } from '../../hooks';
import { EditFlowModal } from '../EditFlowModal';
import { cn } from '@/lib';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useStanRouter } from '@/hooks/useStanRouter';
import { FLOW_ACTIONS } from '../../constants/flowActions';
import { FlowActionsMenu } from './FlowActionsMenu';
import type { FlowRowProps } from './FlowRow.types';

export const FlowRow = React.memo<FlowRowProps>(
    ({ flow, isLoading = false, onClick, className: externalClassName, onDelete }) => {
        const { user } = useUser();
        const { push } = useStanRouter();
        const {
            isEditModalOpen,
            openEditModal,
            closeEditModal,
            handlePublishFlow,
            handleUnpublishFlow,
            handleDuplicateFlow,
            isDuplicating,
        } = useFlowActions();

        // Get flow icon
        const FlowIcon = useMemo(() => {
            return getFlowIcon(flow.trigger || flow.trigger_type || 'checkout');
        }, [flow.trigger, flow.trigger_type]);

        // Get flow status
        const flowStatus = useMemo(() => getFlowStatus(flow), [flow]);

        const isMobile = useIsMobile();

        // Get analytics data
        const analyticsData = useMemo(() => {
            const currency = user?.data?.currency || 'USD';
            return getFlowAnalyticsData(flow, currency, isMobile);
        }, [flow, user?.data?.currency, isMobile]);

        // Build columns
        const columns = useMemo(
            () => buildFlowColumns(flow, FlowIcon, analyticsData, flowStatus, isMobile),
            [flow, FlowIcon, analyticsData, flowStatus, isMobile]
        );

        // Grid template - use single column on mobile, multi-column on desktop
        const gridTemplate = isMobile ? '1fr' : getGridTemplate();

        const handleDropdownAction = (action: string) => {
            const flowId = flow.flow_id;
            const flowName = flow.data?.name || `Flow ${flowId}`;

            switch (action) {
                case FLOW_ACTIONS.EDIT_FLOW:
                    openEditModal();
                    break;
                case FLOW_ACTIONS.EDIT_EMAILS:
                    // Navigate to flow emails - UI level navigation
                    if (flow.flow_emails && flow.flow_emails.length > 0 && flow.flow_emails[0]) {
                        push(`/flows/${flowId}/emails/${flow.flow_emails[0].flow_email_id}`);
                    } else {
                        push(`/flows/${flowId}/new`);
                    }
                    break;
                case FLOW_ACTIONS.PUBLISH:
                    handlePublishFlow(flowId);
                    break;
                case FLOW_ACTIONS.UNPUBLISH:
                    handleUnpublishFlow(flowId);
                    break;
                case FLOW_ACTIONS.DUPLICATE:
                    handleDuplicateFlow(flowId);
                    break;
                case FLOW_ACTIONS.DELETE:
                    onDelete?.(flowId, flowName);
                    break;
                default:
                    console.warn('Unknown action:', action);
            }
        };

        // Action buttons
        const actionButtons: DataRowActionButton[] = [
            {
                id: 'flow-analytics',
                label: 'Flow Analytics',
                onClick: () => {
                    push(`/analytics/flow/${flow.flow_id}`);
                },
                icon: IcAnalytics,
            },
            {
                id: 'flow-actions',
                type: 'dropdown',
                label: 'Flow Actions',
                menu: <FlowActionsMenu flow={flow} onAction={handleDropdownAction} isDuplicating={isDuplicating} />,
            },
        ];

        if (isLoading) {
            return (
                <div className='animate-pulse'>
                    <div className='bg-neutral-secondary h-20 rounded-lg' />
                </div>
            );
        }

        return (
            <>
                <StanDataRow
                    columns={columns}
                    gridTemplate={gridTemplate}
                    variant='simple'
                    actionButtons={actionButtons}
                    onClick={onClick}
                    className={cn('cursor-pointer', isMobile ? 'flow-row-mobile min-h-32' : 'h-20', externalClassName)}
                />

                <EditFlowModal isOpen={isEditModalOpen} flow={flow} onClose={closeEditModal} />
            </>
        );
    }
);

FlowRow.displayName = 'FlowRow';
